import React, { useEffect, useRef, useState } from 'react';
import tinycolor from 'tinycolor2';
import { registerOverlayPortal } from '@puckeditor/core';
import {
  ActionIcon, Button, ColorInput, ColorPicker, Group, Popover, ScrollArea, Stack, Tooltip,
} from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { parseCssColorParts, toCssColor } from '../../utils/cssColor';
import { usePuckRichTextStyle } from '../../context/PuckRichTextStyleContext';
import { isLightColor } from '../../../shared/colors/lightColor';
import DuotoneLightIcon from '../../../components/common/DuotoneLightIcon';
import InspectorRow from './InspectorRow';
import styles from './PuckColorField.module.css';

/** Pastille de nuancier : diamètre et écart, en px. */
const SWATCH_SIZE = 22;
const SWATCH_GAP = 6;
const DROPDOWN_WIDTH = 240;

const TRANSPARENT_SWATCH_STYLE = {
  backgroundImage: 'conic-gradient(#ced4da 90deg, transparent 90deg 180deg, #ced4da 180deg 270deg, transparent 270deg)',
  backgroundSize: '8px 8px',
};

/* Les icônes du classique (cf. ColorControl, HighlightControl,
   NavbarListButton `colorInIcon`) : la couleur courante teinte la lettre ou la
   couche secondaire du duotone, ce qui montre la valeur sans ouvrir le champ. */
const GLYPH_ICONS = {
  fill: { name: 'fill-drip' },
  highlight: { name: 'highlighter', weight: 'solid' },
  stroke: { name: 'border-outer' },
};

const GLYPH_LABELS = {
  text: 'Couleur du texte',
  highlight: 'Surlignage',
};

/** Coche lisible sur la pastille, claire ou foncée. */
const checkColorOn = (color) => (tinycolor(color).isLight() ? '#000000' : '#ffffff');

/** value vide/'transparent' → nuancier affiché en blanc (convention historique). */
function getDisplayParts(value, allowTransparent) {
  if (allowTransparent && (!value || value === 'transparent')) return { hex: '#ffffff', alpha: 100 };
  return parseCssColorParts(value);
}

/** hex + alpha (0–100) → hex8 pour le format="hexa" du ColorPicker. */
function toHex8(hex, alphaPercent) {
  return tinycolor(hex).setAlpha(alphaPercent / 100).toHex8String();
}

function ColorGlyph({ glyph, color }) {
  const outlined = isLightColor(color);
  if (glyph === 'text') {
    return (
      <span
        className={`${styles.textGlyph} ${outlined ? styles.outlined : ''}`.trim()}
        style={color ? { '--color-glyph': color } : undefined}
      >
        A
      </span>
    );
  }
  const { name, weight } = GLYPH_ICONS[glyph] || GLYPH_ICONS.fill;
  return (
    <DuotoneLightIcon
      name={name}
      weight={weight}
      style={{ fontSize: 16 }}
      className={outlined ? styles.outlinedIcon : ''}
      {...(color ? { secondaryColor: color, secondaryOpacity: 1 } : {})}
    />
  );
}

const BUBBLE_POPOVER_PROPS = {
  withinPortal: true,
  zIndex: 10000,
  keepMounted: false,
  floatingStrategy: 'fixed',
  hideDetached: false,
  clickOutsideEvents: ['click'],
};

/**
 * Couleur Puck / webviewer : un bouton-icône teint de la couleur courante,
 * comme dans le classique, qui ouvre la saisie libre (si la règle du document
 * l'autorise) puis la palette sur une seule ligne défilante — à six couleurs
 * et plus, des rangées de pastilles devenaient illisibles.
 * variant="bubble" (toolbar texte riche) : mêmes visuels, mais interactions
 * bubble menu (préservation de la sélection, lock du menu pendant le popover).
 */
export default function PuckColorField({
  value = '',
  onChange,
  swatches = [],
  size = 'xs',
  label,
  hexLabel,
  glyph = 'fill',
  variant = 'default',
  disabled = false,
  placeholder,
  className,
  rememberNow,
  lockBubbleMenu,
  unlockBubbleMenu,
  onRestoreSelection,
  allowTransparent = false,
  /* Certains fonds sont hérités : le bloc n'a pas de couleur à lui, il porte
     celle que le proposal a posée à la génération. Le champ reçoit alors la
     couleur héritée comme valeur — vide n'y veut donc pas dire « transparent »,
     et retirer le fond s'écrit explicitement. */
  emptyIsInherited = false,
}) {
  const { allowFreeColors, canAddPaletteColors, addTemplatePaletteColor } = usePuckRichTextStyle();
  const isBubble = variant === 'bubble';
  const isTransparentValue = allowTransparent && (emptyIsInherited
    ? value === 'transparent'
    : (!value || value === 'transparent'));
  const parsed = getDisplayParts(value, allowTransparent);
  const [hex, setHex] = useState(parsed.hex);
  const [alpha, setAlpha] = useState(parsed.alpha);
  const [opened, setOpened] = useState(false);
  const portalCleanupRef = useRef(null);

  useEffect(() => {
    // Passage à transparent : on garde la dernière couleur affichée dans le
    // picker (le rond coché indique l'état) — dé-cocher transparent la
    // ré-émet telle quelle (handleToggleTransparent).
    if (allowTransparent && (!value || value === 'transparent')) return;
    const next = getDisplayParts(value, allowTransparent);
    setHex(next.hex);
    setAlpha(next.alpha);
  }, [value, allowTransparent]);

  useEffect(() => () => portalCleanupRef.current?.(), []);

  const currentColor = toCssColor(hex, alpha);

  const emit = (nextHex, nextAlpha, { refocusEditor = true } = {}) => {
    const css = toCssColor(nextHex, nextAlpha);
    if (!css) return;
    if (isBubble) onChange?.(css, { refocusEditor });
    else onChange?.(css);
  };

  const stopBubble = (e) => {
    e.stopPropagation();
  };

  const handleToggleTransparent = (e) => {
    e.preventDefault();
    if (isBubble) {
      e.stopPropagation();
      rememberNow?.();
    }
    if (isTransparentValue) {
      emit(hex, alpha, { refocusEditor: !isBubble });
      return;
    }
    // Sans héritage, '' EST la valeur transparente (convention historique).
    const cleared = emptyIsInherited ? 'transparent' : '';
    if (isBubble) onChange?.(cleared, { refocusEditor: !isBubble });
    else onChange?.(cleared);
  };

  // Boutons de nuancier : appliqués au mousedown (pas au click), avec
  // preventDefault — sinon le focus bascule sur le <button> avant que le
  // click ne se déclenche, ce qui fait sortir l'éditeur riche du mode
  // édition (bulle qui se referme) avant que la couleur soit posée.
  const handleSwatchActivate = (color) => (e) => {
    e.preventDefault();
    if (isBubble) {
      e.stopPropagation();
      rememberNow?.();
    }
    emit(color, 100, { refocusEditor: false });
  };

  const isInPalette = swatches.some((color) => tinycolor.equals(color, currentColor));

  const handleAddToPalette = (e) => {
    e.preventDefault();
    if (isBubble) rememberNow?.();
    addTemplatePaletteColor(currentColor);
  };

  const handleBubbleMouseDown = (e) => {
    rememberNow?.();
    e.stopPropagation();
    if (e.target instanceof HTMLInputElement) {
      e.preventDefault();
      e.target.focus();
    }
  };

  const handleOpenedChange = (next) => {
    setOpened(next);
    if (!isBubble) return;
    if (next) {
      rememberNow?.();
      lockBubbleMenu?.();
      window.requestAnimationFrame(() => {
        portalCleanupRef.current?.();
        const dropdowns = document.querySelectorAll('[class*="Popover-dropdown"]');
        const el = dropdowns[dropdowns.length - 1];
        if (el instanceof HTMLElement) {
          portalCleanupRef.current = registerOverlayPortal(el, { disableDragOnFocus: false });
        }
      });
      return;
    }
    portalCleanupRef.current?.();
    portalCleanupRef.current = null;
    unlockBubbleMenu?.();
    window.requestAnimationFrame(() => onRestoreSelection?.());
  };

  // Mousedown sans focus : le bouton ne doit pas voler la sélection de
  // l'éditeur riche avant que le popover ne la mémorise.
  const handleTriggerMouseDown = (e) => {
    if (!isBubble) return;
    e.preventDefault();
    rememberNow?.();
  };

  // Pas de libellé dans le bubble menu — le bouton y est identifié par son
  // icône. label === null : le libellé est porté par un InspectorRow externe
  // (createPuckColorField).
  const resolvedLabel = (isBubble || label === null) ? undefined : (label || hexLabel);

  const popoverProps = isBubble
    ? BUBBLE_POPOVER_PROPS
    : { withinPortal: true, zIndex: 10000, keepMounted: false };

  // Sans nuancier, la règle n'a pas de charte à faire respecter : masquer la
  // saisie laisserait un champ vide, où plus aucune couleur n'est choisissable.
  const showFreeInput = allowFreeColors || swatches.length === 0;

  const updateLocal = (v) => {
    const next = parseCssColorParts(v);
    setHex(next.hex);
    setAlpha(next.alpha);
  };

  const commit = (v) => {
    const next = parseCssColorParts(v);
    setHex(next.hex);
    setAlpha(next.alpha);
    emit(next.hex, next.alpha, { refocusEditor: false });
  };

  const freeInput = showFreeInput && (
    <>
      <ColorPicker
        format="hexa"
        value={toHex8(hex, alpha)}
        onChange={updateLocal}
        onChangeEnd={commit}
        size="sm"
        fullWidth
      />
      <ColorInput
        value={toHex8(hex, alpha)}
        onChange={updateLocal}
        onChangeEnd={commit}
        format="hexa"
        size={size}
        withPicker={false}
        withEyeDropper={false}
        fixOnBlur
        placeholder={placeholder}
        disabled={disabled}
      />
    </>
  );

  const isSelectedSwatch = (color) => !isTransparentValue && tinycolor.equals(color, currentColor);

  const swatchRow = (allowTransparent || swatches.length > 0) && (
    <ScrollArea type="auto" scrollbars="x" scrollbarSize={6} offsetScrollbars="x">
      <Group gap={SWATCH_GAP} wrap="nowrap" py={2}>
        {allowTransparent && (
          <Tooltip label={isTransparentValue ? 'Couleur transparente' : 'Rendre transparent'} withinPortal zIndex={10001}>
            <ActionIcon
              type="button"
              variant="default"
              size={SWATCH_SIZE}
              radius="xl"
              disabled={disabled}
              aria-label="Transparent"
              onMouseDown={handleToggleTransparent}
              style={{ ...TRANSPARENT_SWATCH_STYLE, flexShrink: 0 }}
            >
              {isTransparentValue && <FontAwesomeIcon icon={icon({ name: 'check', style: 'solid' })} style={{ fontSize: 12 }} />}
            </ActionIcon>
          </Tooltip>
        )}
        {swatches.map((color) => (
          <ActionIcon
            key={color}
            type="button"
            variant="default"
            size={SWATCH_SIZE}
            radius="xl"
            disabled={disabled}
            aria-label={color}
            onMouseDown={handleSwatchActivate(color)}
            style={{ backgroundColor: color, flexShrink: 0 }}
          >
            {isSelectedSwatch(color) && (
              <FontAwesomeIcon icon={icon({ name: 'check', style: 'solid' })} style={{ fontSize: 12, color: checkColorOn(color) }} />
            )}
          </ActionIcon>
        ))}
      </Group>
    </ScrollArea>
  );

  const addToPalette = canAddPaletteColors && !isTransparentValue && (
    <Button
      type="button"
      size="xs"
      variant="light"
      disabled={disabled || isInPalette}
      leftSection={<FontAwesomeIcon icon={icon({ name: 'plus', style: 'light' })} />}
      onMouseDown={handleAddToPalette}
    >
      {isInPalette ? 'Déjà dans la palette' : 'Ajouter à la palette'}
    </Button>
  );

  const triggerLabel = GLYPH_LABELS[glyph] || resolvedLabel || 'Couleur';

  const picker = (
    <Popover
      opened={opened}
      onChange={handleOpenedChange}
      position="bottom-start"
      shadow="md"
      {...popoverProps}
    >
      <Popover.Target>
        <ActionIcon
          type="button"
          variant="default"
          size="lg"
          disabled={disabled}
          aria-label={triggerLabel}
          aria-haspopup="dialog"
          onMouseDown={handleTriggerMouseDown}
          onClick={() => handleOpenedChange(!opened)}
          className={isTransparentValue ? styles.transparentTrigger : undefined}
        >
          <ColorGlyph glyph={glyph} color={isTransparentValue ? null : currentColor} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown w={DROPDOWN_WIDTH} onMouseDown={isBubble ? undefined : stopBubble}>
        <Stack gap={8}>
          {freeInput}
          {swatchRow}
          {addToPalette}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );

  if (isBubble) {
    return (
      <Group gap={8} wrap="nowrap" align="center" className={className} onMouseDown={handleBubbleMouseDown}>
        {picker}
      </Group>
    );
  }

  if (!resolvedLabel) {
    return <div className={className} onMouseDown={stopBubble}>{picker}</div>;
  }

  return (
    <div className={className} onMouseDown={stopBubble}>
      <InspectorRow label={resolvedLabel}>{picker}</InspectorRow>
    </div>
  );
}
